import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, CheckCircle2, Eye, EyeOff, Archive, Trash2, Copy, ExternalLink, X } from "lucide-react";
import { api } from "../lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED  = "#A32D2D";
const GREY = "#5a5d96";

const StatusBadge = ({ status, featured }) => {
  const colors = { pending: GOLD, approved: GREEN, archived: GREY };
  return (
    <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <span style={{
        background: `${colors[status] || GREY}22`, color: colors[status] || GREY,
        padding: "3px 8px", borderRadius: 4, fontSize: 11, letterSpacing: 1,
        fontFamily: "Bebas Neue, sans-serif",
      }}>
        {status?.toUpperCase()}
      </span>
      {featured && (
        <span style={{
          background: `${GOLD}22`, color: GOLD, padding: "3px 8px", borderRadius: 4,
          fontSize: 11, letterSpacing: 1, fontFamily: "Bebas Neue, sans-serif",
        }}>
          ⭐ FEATURED
        </span>
      )}
    </div>
  );
};

const StarsDisplay = ({ n }) => (
  <div style={{ display: "inline-flex", gap: 2 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={16} fill={i < n ? GOLD : "transparent"} color={i < n ? GOLD : "#cbd0db"} />
    ))}
  </div>
);

const AdminMarketing = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("pending");  // pending | approved | archived | all
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const r = await api.get(`/admin/review-wins${params}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      setFlash(`Load failed: ${e?.message}`);
    } finally {
      setBusy(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    setBusy(true);
    try {
      await api.patch(`/admin/review-wins/${id}`, patch);
      setFlash(`Saved ✓`);
      setTimeout(() => setFlash(null), 1500);
      await load();
    } catch (e) {
      setFlash(`Save failed: ${e?.message}`);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review milestone permanently?")) return;
    try {
      await api.delete(`/admin/review-wins/${id}`);
      await load();
    } catch (e) { setFlash(`Delete failed: ${e?.message}`); }
  };

  const copyForSocial = (item) => {
    const text = `"${item.quote}"\n\n— ${item.customer_name || "Verified customer"}\nfor ${item.contractor_name}, ${item.contractor_trade || ""} in ${item.contractor_city || "Rhode Island"}\n\n${"⭐".repeat(item.stars || 0)}\n\nNearNow · Every Missed Call Is a Missed Check. nearnow.biz`;
    navigator.clipboard?.writeText(text);
    setFlash("Copied — ready to paste into LinkedIn / Twitter / Meta Ad copy.");
    setTimeout(() => setFlash(null), 3000);
  };

  return (
    <div data-testid="admin-marketing-page" style={{ background: "#F8F9FA", minHeight: "100vh" }}>
      <section style={{
        background: `linear-gradient(180deg, ${NAVY} 0%, #060d1a 100%)`,
        color: "#fff", padding: "28px 16px 22px", borderBottom: `3px solid ${GOLD}`,
      }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", flexWrap: "wrap",
                     gap: 12, alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            <p style={{ color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 4, margin: 0, fontSize: 12 }}>
              ADMIN · MARKETING SHOWCASE
            </p>
            <h1 style={{ fontFamily: "Bebas Neue, sans-serif",
                         fontSize: "clamp(30px, 6vw, 52px)", letterSpacing: 1,
                         margin: "4px 0 4px", lineHeight: 1.05 }}>
              Review Wins
            </h1>
            <p style={{ color: "#cbd0db", fontSize: 14, margin: 0 }}>
              Approve + curate → public testimonials & social-media-ready copy.
            </p>
          </div>
          <button data-testid="add-review-manual" onClick={() => setShowAdd(true)}
            className="btn-gold tap" style={{ minHeight: 48, padding: "0 18px" }}>
            + LOG REVIEW MANUALLY
          </button>
        </div>
      </section>

      {/* Counts */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: "18px 16px 0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          {[
            ["pending", GOLD],
            ["approved", GREEN],
            ["archived", GREY],
            ["featured", GOLD],
          ].map(([k, color]) => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setFilter(k === "featured" ? "approved" : k)}
              className="tap"
              style={{
                background: "#fff", color: NAVY, border: filter === k ? `2px solid ${color}` : `1px solid #e5e7ee`,
                borderLeft: `4px solid ${color}`, padding: "10px 14px", borderRadius: 8,
                textAlign: "left", cursor: "pointer", minHeight: 56,
              }}
            >
              <div style={{ color: GREY, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>{k}</div>
              <div style={{ color, fontFamily: "Bebas Neue, sans-serif", fontSize: 26, letterSpacing: 1, lineHeight: 1 }}>
                {counts[k] ?? 0}
              </div>
            </button>
          ))}
        </div>
      </section>

      {flash && (
        <div data-testid="admin-flash" style={{
          maxWidth: 1280, margin: "12px auto 0", padding: "0 16px",
        }}>
          <div style={{ background: GOLD, color: NAVY, padding: "10px 14px", borderRadius: 6 }}>
            {flash}
          </div>
        </div>
      )}

      {/* List */}
      <section style={{ maxWidth: 1280, margin: "16px auto 60px", padding: "0 16px" }}>
        {busy && <p style={{ color: GREY, textAlign: "center" }}>Loading…</p>}
        {!busy && items.length === 0 && (
          <div data-testid="empty" style={{
            background: "#fff", border: `2px dashed ${GOLD}55`, padding: 28, borderRadius: 14,
            textAlign: "center", color: GREY, marginTop: 12,
          }}>
            <Star color={GOLD} size={36} />
            <p style={{ margin: "10px 0 4px", color: NAVY, fontSize: 18, fontWeight: 600 }}>
              No review wins {filter !== "all" ? `with status "${filter}"` : "yet"}.
            </p>
            <p style={{ margin: 0, fontSize: 14 }}>
              Contractors log them via Jobs Today. Or click "LOG REVIEW MANUALLY" above.
            </p>
          </div>
        )}
        <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
          {items.map((it) => (
            <WinCard key={it.id} item={it} onUpdate={update} onRemove={remove} onCopy={copyForSocial} />
          ))}
        </div>
      </section>

      <AnimatePresence>
        {showAdd && <AdminAddModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
      </AnimatePresence>
    </div>
  );
};

const WinCard = ({ item, onUpdate, onRemove, onCopy }) => {
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const begin = () => { setEdits({ quote: item.quote, customer_name: item.customer_name, stars: item.stars }); setEditing(true); };
  const save = () => { onUpdate(item.id, edits); setEditing(false); };

  return (
    <motion.div
      data-testid={`win-${item.id}`}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: "#fff", borderRadius: 12, padding: 16,
        borderLeft: `4px solid ${item.status === "approved" ? GREEN : item.status === "archived" ? GREY : GOLD}`,
        boxShadow: "0 6px 16px rgba(10,22,40,0.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <StarsDisplay n={editing ? edits.stars : item.stars} />
            <StatusBadge status={item.status} featured={item.featured_publicly} />
            <span style={{ fontSize: 12, color: GREY }}>
              {(item.review_date || (item.created_at || "")).slice(0, 10)}
            </span>
          </div>
          <h3 style={{ margin: "8px 0 4px", color: NAVY, fontSize: 16 }}>
            {item.contractor_name} <span style={{ color: GREY, fontWeight: 400, fontSize: 13 }}>
              {item.contractor_trade ? `· ${item.contractor_trade}` : ""}
              {item.contractor_city ? ` · ${item.contractor_city}` : ""}
            </span>
          </h3>
          {editing ? (
            <>
              <label style={{ fontSize: 12, color: GREY }}>Stars
                <input type="number" min="1" max="5" value={edits.stars}
                  className="nn-input" style={{ marginLeft: 6, width: 80, minHeight: 38 }}
                  onChange={(e) => setEdits({ ...edits, stars: Number(e.target.value) })} />
              </label>
              <input className="nn-input" placeholder="Customer name"
                value={edits.customer_name || ""} style={{ marginTop: 8 }}
                onChange={(e) => setEdits({ ...edits, customer_name: e.target.value })} />
              <textarea className="nn-input" rows={3} placeholder="Review text"
                value={edits.quote || ""} style={{ marginTop: 8 }}
                onChange={(e) => setEdits({ ...edits, quote: e.target.value })} />
            </>
          ) : (
            <>
              <p style={{ color: NAVY, fontSize: 14, margin: "6px 0", fontStyle: "italic", lineHeight: 1.55 }}>
                "{item.quote}"
              </p>
              <p style={{ color: GREY, fontSize: 12, margin: 0 }}>
                — {item.customer_name || "Customer"}
                {item.source_url && (
                  <a href={item.source_url} target="_blank" rel="noreferrer"
                     style={{ marginLeft: 8, color: GREEN, textDecoration: "none" }}>
                    view on google <ExternalLink size={11} style={{ display: "inline" }} />
                  </a>
                )}
              </p>
            </>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 160 }}>
          {editing ? (
            <>
              <button data-testid={`save-${item.id}`} onClick={save}
                className="btn-gold tap" style={{ minHeight: 40, fontSize: 12 }}>SAVE</button>
              <button onClick={() => setEditing(false)}
                className="tap" style={btnOutline(GREY)}>CANCEL</button>
            </>
          ) : (
            <>
              {item.status !== "approved" && (
                <button data-testid={`approve-${item.id}`}
                  onClick={() => onUpdate(item.id, { status: "approved" })}
                  className="btn-gold tap" style={{ minHeight: 40, fontSize: 12,
                    display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                  <CheckCircle2 size={14} /> APPROVE
                </button>
              )}
              <button data-testid={`feature-${item.id}`}
                onClick={() => onUpdate(item.id, { featured_publicly: !item.featured_publicly,
                                                  status: item.status === "approved" ? "approved" : "approved" })}
                className="tap" style={btnOutline(item.featured_publicly ? GOLD : NAVY)}>
                {item.featured_publicly ? <><EyeOff size={14} /> UNFEATURE</> : <><Eye size={14} /> FEATURE PUBLICLY</>}
              </button>
              <button data-testid={`copy-${item.id}`} onClick={() => onCopy(item)}
                className="tap" style={btnOutline(GREEN)}>
                <Copy size={14} /> COPY FOR SOCIAL
              </button>
              <button data-testid={`edit-${item.id}`} onClick={begin}
                className="tap" style={btnOutline(GREY)}>EDIT</button>
              {item.status !== "archived" && (
                <button data-testid={`archive-${item.id}`}
                  onClick={() => onUpdate(item.id, { status: "archived" })}
                  className="tap" style={btnOutline(GREY)}>
                  <Archive size={14} /> ARCHIVE
                </button>
              )}
              <button data-testid={`delete-${item.id}`} onClick={() => onRemove(item.id)}
                className="tap" style={btnOutline(RED)}>
                <Trash2 size={14} /> DELETE
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const btnOutline = (color) => ({
  background: "transparent", color, border: `1px solid ${color}`,
  borderRadius: 6, padding: "8px 10px", minHeight: 40,
  fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1.2, fontSize: 12,
  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center",
});

const AdminAddModal = ({ onClose, onCreated }) => {
  const [contractorId, setContractorId] = useState("");
  const [stars, setStars] = useState(5);
  const [customerName, setCustomerName] = useState("");
  const [quote, setQuote] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!contractorId.trim()) { setErr("contractor_id required"); return; }
    if (!quote.trim()) { setErr("quote required"); return; }
    setBusy(true); setErr(null);
    try {
      await api.post(`/contractor/${contractorId.trim()}/review-wins`, {
        stars, customer_name: customerName, quote, source_url: sourceUrl || null,
      });
      onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, background: "rgba(10,22,40,0.65)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 100 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%", maxWidth: 560 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontFamily: "Bebas Neue, sans-serif", fontSize: 26, color: NAVY }}>
            LOG REVIEW MANUALLY
          </h3>
          <button onClick={onClose} className="tap"
            style={{ background: "transparent", border: "none", padding: 6, cursor: "pointer" }}>
            <X size={22} color={NAVY} />
          </button>
        </div>
        <p style={{ color: GREY, fontSize: 13, marginTop: 0 }}>
          For when a contractor sends you a screenshot or you spot a fresh Google review yourself.
        </p>
        <FF label="Contractor ID *">
          <input className="nn-input" value={contractorId} onChange={(e) => setContractorId(e.target.value)} />
        </FF>
        <FF label="Stars">
          <input type="number" min="1" max="5" className="nn-input"
            value={stars} onChange={(e) => setStars(Number(e.target.value))} />
        </FF>
        <FF label="Customer Name">
          <input className="nn-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
        </FF>
        <FF label="Quote *">
          <textarea className="nn-input" rows={4} value={quote} onChange={(e) => setQuote(e.target.value)} />
        </FF>
        <FF label="Google Review URL (optional)">
          <input className="nn-input" value={sourceUrl} placeholder="https://maps.google.com/..."
            onChange={(e) => setSourceUrl(e.target.value)} />
        </FF>
        {err && <p style={{ color: RED, fontSize: 13 }}>{String(err)}</p>}
        <button data-testid="admin-add-submit" onClick={submit} disabled={busy}
          className="btn-gold tap" style={{ width: "100%", minHeight: 52, fontSize: 16 }}>
          {busy ? "SAVING…" : "SAVE — LANDS IN PENDING"}
        </button>
      </motion.div>
    </motion.div>
  );
};

const FF = ({ label, children }) => (
  <div style={{ marginBottom: 10 }}>
    <label className="nn-label" style={{ display: "block", marginBottom: 6, color: NAVY }}>{label}</label>
    {children}
  </div>
);

export default AdminMarketing;
